import { useEffect, useMemo, useState } from "react"

import { load } from "scrivito"
import { ProductObj } from "scrivito/Objs/Product/ProductObjClass"
import { loadProductWithHighestMatch } from "shared/lib/product-obj/loadProduct"

import { useProductObjCache } from "entities/products/model/store/useProductObjCache"

import { useProductBaseVariants } from "./useProductBaseVariants"
import { useVariantKey } from "./useVariantKey"

const EMPTY_EXPERT_ATTRIBUTES: string[] = []
const EMPTY_HIDDEN_ATTRIBUTES: string[] = []

export function useProductObjLoad(variantKey: string) {
  const { productType } = useVariantKey(variantKey)
  const [productObj, setProductObj] = useState<ProductObj | null | undefined>(
    undefined
  )
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { saveProductObjCache, getCacheForVariantKey } = useProductObjCache()

  const [hasProductBaseVariants, setHasProductBaseVariants] = useState<
    boolean | undefined
  >(undefined)

  const cache = useMemo(
    () => (variantKey ? getCacheForVariantKey(variantKey) : undefined),
    [getCacheForVariantKey, variantKey]
  )

  useEffect(() => {
    setIsLoading(true)
  }, [productType])

  useEffect(() => {
    if (variantKey) {
      loadProductWithHighestMatch({
        variantKey,
        saveCache: saveProductObjCache,
        skipCache: !!cache
      }).then(obj => {
        if (!obj) {
          setProductObj(null)
        }
        if (obj && obj.id() !== productObj?.id()) {
          setProductObj(obj)
        }
        setIsLoading(false)
      })
    }
  }, [variantKey])

  const expertAttributes =
    (productObj?.get("expertAttributes") as string[]) || EMPTY_EXPERT_ATTRIBUTES
  const hiddenAttributes =
    (productObj?.get("hiddenAttributes") as string[]) || EMPTY_HIDDEN_ATTRIBUTES
  const standardConfiguration =
    (productObj?.get("standardConfiguration") as string) || ""

  useEffect(() => {
    if (productObj) {
      if (productObj?.get("hideConfigurationPresets")) {
        setHasProductBaseVariants(false)
      } else if (standardConfiguration) {
        load(() => productObj.children()).then(presets => {
          if (Array.isArray(presets)) {
            setHasProductBaseVariants(presets.length > 0)
          } else {
            setHasProductBaseVariants(false)
          }
        })
      } else {
        setHasProductBaseVariants(false)
      }
    } else if (!isLoading) {
      setHasProductBaseVariants(false)
    }
  }, [productObj, isLoading])

  const { highestMatchingProductBaseVariantObj } = useProductBaseVariants({
    productObj,
    variantKey
  })

  return {
    productType,
    title: cache?.title || productObj?.get("title"),
    titleShort: cache?.titleShort || productObj?.get("titleShort"),
    isConfigurationWip: productObj?.get("isConfigurationWIP"),
    kind: productObj?.get("kind"),
    highestMatchingProductBaseVariantObj,
    productObj,
    isLoadingObj: isLoading,
    expertAttributes,
    hiddenAttributes,
    standardConfiguration,
    hasProductBaseVariants
  }
}
